import {Component, inject, input, model, resource} from '@angular/core';
import {take} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {DxDataGridModule, DxLoadPanelModule, DxPopupModule} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';
import {SharedModule} from '../../../../shared/shared.module';
import {AvoirExterneDTO} from '../../../../core/dtos/facturation/fournisseurs/avoirs/avoir-externe-dto';
import {AvoirExterneRapprochementDTO} from '../../../../core/dtos/facturation/fournisseurs/avoirs/avoir-externe-rapprochement-dto';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {RapprochementAvoirExterneStatutEnum, RAPPROCHEMENT_AVOIR_EXTERNE_STATUT_LABELS} from '../../../../core/enums/facturation/fournisseur/rapprochement-avoir-externe-statut-enum';
import {AvoirExterneStatutEnum, AVOIR_EXTERNE_STATUT_LABELS} from '../../../../core/enums/facturation/fournisseur/avoir-externe-statut-enum';
import {AvoirExterneService} from '../../../../core/services/entities/avoir-externe.service';
import {FactureExterneStatutEnum} from "../../../../core/enums/facturation/fournisseur/facture-externe-statut-enum";

@Component({
  selector: 'yo-avoir-externe-dialog',
  templateUrl: './avoir-externe-dialog.component.html',
  styleUrls: ['./avoir-externe-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, DxPopupModule, DxDataGridModule, DxLoadPanelModule, SharedModule]
})
export class AvoirExterneDialogComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;
  protected readonly RapprochementStatutEnum = RapprochementAvoirExterneStatutEnum;
  protected readonly AvoirExterneStatutEnum = AvoirExterneStatutEnum;
  protected readonly avoirStatutLabels = AVOIR_EXTERNE_STATUT_LABELS;
  protected readonly rapprochementStatutLabels = RAPPROCHEMENT_AVOIR_EXTERNE_STATUT_LABELS;

  private readonly avoirExterneSvc = inject(AvoirExterneService);

  readonly displayDialog = model<boolean>(false);
  readonly avoirId = input<number | null>(null);
  readonly dialogTitle = input<string>('Avoir externe');

  readonly avoirResource = resource<AvoirExterneDTO | null, number | null>({
    params: () => this.avoirId(),
    loader: async ({params: id}) => {
      if (!id) return null;
      return this.avoirExterneSvc.findById(id).pipe(take(1)).toPromise();
    }
  });

  getRapprochementTvaTaux = (avoir: AvoirExterneDTO, slot: 1 | 2 | 3 | 4 | 5): number | null => {
    const key = `tva${slot}Taux` as keyof AvoirExterneRapprochementDTO;
    return avoir.rapprochementList?.find(r => r[key] != null)?.[key] as number ?? null;
  };

  closeDialog = (): void => this.displayDialog.set(false);

  validerEcart = async (): Promise<void> => {
    const avoir = this.avoirResource.value();
    if (!avoir) return;
    const isValidated: boolean = await confirm(`Voulez-vous valider l'écart de l'avoir externe n° ${avoir.numero} ?`, "Validation de l'écart");
    if (!isValidated) return;
    this.closeDialog();
    this.avoirExterneSvc.validerEcart(avoir.id).subscribe(() => {
      this.avoirExterneSvc.avoirExterneResource.update(list =>
        (list ?? []).map(a => a.id === avoir.id
          ? {...a, rapprochementStatut: RapprochementAvoirExterneStatutEnum.ECART_VALIDE}
          : a
        )
      );
      this.avoirResource.update(a => a ? {...a, rapprochementStatut: RapprochementAvoirExterneStatutEnum.ECART_VALIDE} : a);
    });
  };

  abandonner = async (): Promise<void> => {
    const avoir = this.avoirResource.value();
    if (!avoir) return;
    const isConfirmed: boolean = await confirm(`Voulez-vous abandonner l'avoir externe n° ${avoir.numero} ?`, "Abandon de l'avoir");
    if (!isConfirmed) return;
    this.closeDialog();
    this.avoirExterneSvc.abandonner(avoir.id).subscribe(() => {
      this.avoirExterneSvc.avoirExterneResource.update(list =>
        (list ?? []).map(a => a.id === avoir.id
          ? {...a, statut: AvoirExterneStatutEnum.ABANDONNEE}
          : a
        )
      );
    });
  };

  refuser = async (): Promise<void> => {
    const avoir = this.avoirResource.value();
    if (!avoir) return;
    const isConfirmed: boolean = await confirm(`Voulez-vous refuser l'avoir externe n° ${avoir.numero} ?`, "Refus de l'avoir");
    if (!isConfirmed) return;
    this.closeDialog();
    this.avoirExterneSvc.refuser(avoir.id).subscribe(() => {
      this.avoirExterneSvc.avoirExterneResource.update(list =>
        (list ?? []).map(a => a.id === avoir.id
          ? {...a, statut: AvoirExterneStatutEnum.REFUSEE}
          : a
        )
      );
    });
  };

  protected validerRapprochement = async (): Promise<void> => {
    const avoir = this.avoirResource.value();
    if (!avoir) return;
    const isValidated: boolean = await confirm(`Voulez-vous valider l'avoir externe n° ${avoir.numero} ?`, "Validation de l'avoir");
    if (!isValidated) return;
    this.closeDialog();
    this.avoirExterneSvc.valider(avoir.id).subscribe(() => {
      this.avoirExterneSvc.avoirExterneResource.update(list =>
        (list ?? []).map(a => a.id === avoir.id
          ? {...a, statut: AvoirExterneStatutEnum.VALIDEE_MANUELLEMENT}
          : a
        )
      );
      this.avoirResource.update(a => a ? {...a, statut: AvoirExterneStatutEnum.VALIDEE_MANUELLEMENT} : a);
    });
  };
  protected readonly FactureExterneStatutEnum = FactureExterneStatutEnum;
}
